import { useLocation } from 'react-router-dom';
import { useSearchData } from '@client/catalog/hooks';
import { FILTERS, getFacetFilterCheckedValues, getIsActiveFilter } from '@client/catalog/utils/facets';
import { getFiltersValuesFromSearchParams } from '@client/catalog/utils/filters';
import { FilterGroup, FiltersValuesType } from 'b2b-common/core/catalog/Catalog.types';

type UseCatalogFacetFiltersDataReturnType = {
  availableFacetFilters: FilterGroup[];
  activeFacetFilters: FiltersValuesType;
  activeFacetFiltersCount: number;
  facetFiltersWithDefaults: FiltersValuesType;
};

export const useCatalogFacetFiltersData = (hiddenSections?: string[]): UseCatalogFacetFiltersDataReturnType => {
  const { search } = useLocation();
  const { data } = useSearchData();
  const catalogFilters = data?.filters || [];
  const facetFilters = getFiltersValuesFromSearchParams(FILTERS.facetFilters, search);

  const availableFacetFilters: FilterGroup[] = catalogFilters?.filter(
    (cf: FilterGroup) => !hiddenSections?.includes?.(cf.field),
  ) || [];

  const activeFacetFilters: FiltersValuesType = availableFacetFilters
    .reduce((facetFiltersActive: FiltersValuesType, facetFilter: FilterGroup) => {
      const { field, name } = facetFilter;

      if (getIsActiveFilter(facetFilter)) {
        facetFiltersActive[field] = {
          value: getFacetFilterCheckedValues(facetFilter),
          name,
        };
      }

      return facetFiltersActive;
    }, {});

  const activeFacetFiltersCount = Object.entries(activeFacetFilters)
    .reduce((numberOfFilters, entry) => {
      const filterValue = entry[1];
      if (Array.isArray(filterValue.value)) {
        return numberOfFilters + filterValue.value.length;
      }

      return ++numberOfFilters;
    }, 0);

  const facetFiltersWithDefaults = Object
    .entries(facetFilters)
    .reduce((filters, [name, value]) => {
      if (Array.isArray(value)) {
        return { ...filters, [name]: value };
      }

      const correspondingFilter = availableFacetFilters.find(filter => filter.name === name);

      if (!correspondingFilter || !correspondingFilter.rangeSlider) {
        return { ...filters, [name]: value };
      }

      return {
        ...filters,
        [name]: {
          min: value.min || correspondingFilter.slider.minValue,
          max: value.max || correspondingFilter.slider.maxValue,
        },
      };
    }, {});

  return {
    availableFacetFilters,
    activeFacetFilters,
    activeFacetFiltersCount,
    facetFiltersWithDefaults,
  };
};
